// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetPreferredPackageVersion } from '@hooks/queries/useGetPreferredPackageVersion';
import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Card, CardContent, IconButton, Skeleton, Typography } from '@mui/material';
import { useCopyToClipboard } from '../../../components/useCopyToClipboard';
import { PackageName } from '../../../config/package';

interface PackageDisplayProps {
  packageName: PackageName;
}
export const PackageDisplay: React.FC<PackageDisplayProps> = ({ packageName }) => {
  const p = useGetPreferredPackageVersion(packageName);
  const copy = useCopyToClipboard();
  const fields = [
    { name: 'package name', value: p.data?.packagePreference?.packageReference?.packageName },
    { name: 'package ID', value: p.data?.packagePreference?.packageReference?.packageId },
    { name: 'synchronizer Id', value: p.data?.packagePreference?.synchronizerId },
  ];
  const copyContent = {
    packageName: p.data?.packagePreference?.packageReference?.packageName,
    packageId: p.data?.packagePreference?.packageReference?.packageId,
    packageVersion: p.data?.packagePreference?.packageReference?.packageVersion,
    synchronizerId: p.data?.packagePreference?.synchronizerId,
  };
  const onCopy = () => {
    copy(JSON.stringify(copyContent, null, 2));
  };
  const displayedFields = fields.map((f) => (
    <Box
      key={f.name}
      sx={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body2"
        sx={{ textTransform: 'capitalize', mr: 1 }}
        color="text.secondary"
        fontWeight="bold"
      >
        {f.name}:
      </Typography>
      <Typography
        sx={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          pl: 0.5,
          pr: 0.5,
          '&:hover': {
            background: 'grey',
            color: 'secondary.contrastText',
          },
        }}
        variant="body2"
        fontWeight="bold"
        onClick={f.value ? () => copy(f.value!) : undefined}
      >
        {f.value}
      </Typography>
    </Box>
  ));
  if (p.isLoading) {
    return <Skeleton sx={{ height: 130, borderRadius: 2 }} variant="rectangular" />;
  }
  if (!p.data) {
    return (
      <Card sx={{ display: 'flex', flexDirection: 'row', p: 2 }} variant="outlined">
        <ErrorIcon color="action" sx={{ mr: 1 }} />
        <Typography color="text.secondary">Not installed</Typography>
      </Card>
    );
  }
  const packageVersion = p.data.packagePreference?.packageReference?.packageVersion;
  return (
    <Card variant="outlined">
      <CardContent sx={{ position: 'relative' }}>
        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography sx={{ mr: 1 }} fontWeight="bold">
            Package Version:
          </Typography>
          <Typography
            sx={{ cursor: 'pointer' }}
            onClick={() => copy(packageVersion || '')}
            fontWeight="bold"
          >
            {p.data.packagePreference?.packageReference?.packageVersion}
          </Typography>
        </Box>
        {displayedFields}

        <IconButton
          onClick={onCopy}
          sx={{ m: 2, position: 'absolute', top: 0, right: 0 }}
          size="small"
        >
          <ContentCopyIcon color="action" fontSize="small" />
        </IconButton>
      </CardContent>
    </Card>
  );
};
